import React, { useEffect, useState, Component } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSourcing } from '../../contexts/SourcingContext';
import { CheckCircleIcon, XCircleIcon, ClipboardCheckIcon, CalendarIcon, DollarSignIcon, EditIcon, SaveIcon, XIcon, ChevronLeftIcon, ChevronRightIcon, PlusIcon, UserIcon, TagIcon } from 'lucide-react';
const ReviewScreen: React.FC = () => {
  const navigate = useNavigate();
  const {
    sourcingEvent,
    updateEventStatus,
    toggleSupplierSelection
  } = useSourcing();
  // Edit states
  const [editingTitle, setEditingTitle] = useState(false);
  const [editingDescription, setEditingDescription] = useState(false);
  const [editingRequirements, setEditingRequirements] = useState(false);
  const [editingDate, setEditingDate] = useState(false);
  const [editingBudget, setEditingBudget] = useState(false);
  const [editingQuantity, setEditingQuantity] = useState(false);
  const [showSupplierModal, setShowSupplierModal] = useState(false);
  // Form values
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [requirementsText, setRequirementsText] = useState('');
  const [deadline, setDeadline] = useState<Date | null>(null);
  const [budget, setBudget] = useState<number | string>('');
  const [quantity, setQuantity] = useState<number | string>('');
  // Calendar state
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  useEffect(() => {
    if (sourcingEvent) {
      setTitle(sourcingEvent.requirements.title);
      setDescription(sourcingEvent.requirements.description);
      // CRITICAL FIX: Prioritize specifications as the primary source of detailed requirements
      // This ensures that specifications are always shown in the detailed requirements field
      const detailedRequirements = sourcingEvent.requirements.specifications || sourcingEvent.requirements.requirementsText || '';
      setRequirementsText(detailedRequirements);
      // Add quantity state initialization
      if (sourcingEvent.requirements.quantity !== undefined && sourcingEvent.requirements.quantity !== null) {
        console.log('Setting quantity from sourcingEvent:', sourcingEvent.requirements.quantity);
        setQuantity(sourcingEvent.requirements.quantity);
      } else {
        console.log('No quantity in sourcingEvent, setting empty string');
        setQuantity('');
      }
      // Debug log to verify data received in ReviewScreen
      const selectedSuppliers = sourcingEvent.suppliers.filter(s => s.selected);
      console.log('ReviewScreen received:', {
        supplierCount: sourcingEvent.suppliers.length,
        selectedCount: selectedSuppliers.length,
        selectedSupplierNames: selectedSuppliers.map(s => s.name),
        budget: sourcingEvent.requirements.budget,
        budgetType: typeof sourcingEvent.requirements.budget,
        quantity: sourcingEvent.requirements.quantity,
        quantityType: typeof sourcingEvent.requirements.quantity,
        specifications: sourcingEvent.requirements.specifications,
        requirementsText: sourcingEvent.requirements.requirementsText,
        detailedRequirementsUsed: detailedRequirements
      });
      // Handle deadline which could be a Date or string
      if (sourcingEvent.requirements.deadline) {
        if (sourcingEvent.requirements.deadline instanceof Date) {
          setDeadline(sourcingEvent.requirements.deadline);
          setSelectedDate(sourcingEvent.requirements.deadline);
        } else if (typeof sourcingEvent.requirements.deadline === 'string') {
          const parsedDate = new Date(sourcingEvent.requirements.deadline);
          if (!isNaN(parsedDate.getTime())) {
            setDeadline(parsedDate);
            setSelectedDate(parsedDate);
          }
        }
      }
      // CRITICAL FIX: Ensure budget is properly set from the sourcingEvent
      if (sourcingEvent.requirements.budget !== undefined && sourcingEvent.requirements.budget !== null) {
        console.log('Setting budget from sourcingEvent:', sourcingEvent.requirements.budget);
        setBudget(sourcingEvent.requirements.budget);
      } else {
        console.log('No budget in sourcingEvent, setting empty string');
        setBudget('');
      }
    }
  }, [sourcingEvent]);
  if (!sourcingEvent) {
    return <div data-id="element-479">No active sourcing event. Please start a new one.</div>;
  }
  // Make sure we're properly filtering selected suppliers
  const selectedSuppliers = sourcingEvent.suppliers.filter(supplier => supplier.selected);
  const availableSuppliers = sourcingEvent.suppliers.filter(supplier => !supplier.selected);
  const handleLaunchEvent = () => {
    updateEventStatus('live');
    navigate('/sourcing/dashboard');
  };
  const handleGoBack = () => {
    navigate('/sourcing/selection');
  };
  // Calendar functions
  const getDaysInMonth = (year: number, month: number) => {
    return new Date(year, month + 1, 0).getDate();
  };
  const getFirstDayOfMonth = (year: number, month: number) => {
    return new Date(year, month, 1).getDay();
  };
  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };
  const prevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };
  const handleDateSelection = (day: number) => {
    const newDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    setSelectedDate(newDate);
  };
  const confirmDateSelection = () => {
    if (selectedDate) {
      setDeadline(selectedDate);
      // Update the sourcingEvent context
      if (sourcingEvent) {
        sourcingEvent.requirements.deadline = selectedDate;
      }
      setEditingDate(false);
    }
  };
  // Save functions
  const saveTitle = () => {
    if (sourcingEvent) {
      sourcingEvent.requirements.title = title;
      setEditingTitle(false);
    }
  };
  const saveDescription = () => {
    if (sourcingEvent) {
      sourcingEvent.requirements.description = description;
      setEditingDescription(false);
    }
  };
  // Update the saveRequirements function to properly handle specifications
  const saveRequirements = () => {
    if (sourcingEvent) {
      // CRITICAL FIX: Update both requirementsText and specifications to keep them in sync
      sourcingEvent.requirements.requirementsText = requirementsText;
      sourcingEvent.requirements.specifications = requirementsText;
      console.log('Saved requirements/specifications:', {
        requirementsText: sourcingEvent.requirements.requirementsText,
        specifications: sourcingEvent.requirements.specifications
      });
      setEditingRequirements(false);
    }
  };
  // Add a saveBudget function that properly handles the budget type
  const saveBudget = () => {
    if (sourcingEvent) {
      // Convert to number if possible
      const numBudget = typeof budget === 'string' ? parseFloat(budget) : budget;
      // Log the budget conversion for debugging
      console.log('Saving budget:', {
        original: budget,
        converted: numBudget,
        type: typeof numBudget,
        isNaN: isNaN(Number(numBudget))
      });
      // Update the budget in the sourcingEvent
      sourcingEvent.requirements.budget = !isNaN(Number(numBudget)) ? Number(numBudget) : undefined;
      setEditingBudget(false);
    }
  };
  // Add saveQuantity function
  const saveQuantity = () => {
    if (sourcingEvent) {
      // Convert to number if possible
      const numQuantity = typeof quantity === 'string' ? parseInt(quantity as string) : quantity;
      // Log the quantity conversion for debugging
      console.log('Saving quantity:', {
        original: quantity,
        converted: numQuantity,
        type: typeof numQuantity,
        isNaN: isNaN(Number(numQuantity))
      });
      // Update the quantity in the sourcingEvent
      sourcingEvent.requirements.quantity = !isNaN(Number(numQuantity)) ? Number(numQuantity) : undefined;
      setEditingQuantity(false);
    }
  };
  // Function to safely format date with extensive fallbacks
  const formatDate = (dateValue: any) => {
    if (!dateValue) return 'Not specified';
    try {
      // If it's already a Date object
      if (dateValue instanceof Date) {
        return dateValue.toLocaleDateString();
      }
      // If it's a string (possibly ISO format)
      if (typeof dateValue === 'string') {
        const date = new Date(dateValue);
        if (!isNaN(date.getTime())) {
          return date.toLocaleDateString();
        }
        return dateValue; // Just return the string if we can't parse it
      }
      // Fallback for any other case
      return String(dateValue);
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Error displaying date';
    }
  };
  // Function to safely format budget with extensive fallbacks
  const formatBudget = (budgetValue: any) => {
    if (budgetValue === null || budgetValue === undefined) return 'Not specified';
    try {
      // If it's a number
      if (typeof budgetValue === 'number') {
        return `€${budgetValue.toLocaleString()}`;
      }
      // If it's a string that can be converted to a number
      if (typeof budgetValue === 'string') {
        const budget = Number(budgetValue.replace(/[^0-9.]/g, ''));
        if (!isNaN(budget)) {
          return `€${budget.toLocaleString()}`;
        }
        return budgetValue; // Return the original string if conversion fails
      }
      // Fallback
      return String(budgetValue);
    } catch (error) {
      console.error('Error formatting budget:', error);
      return 'Error displaying budget';
    }
  };
  // Add function to safely format quantity
  const formatQuantity = (quantityValue: any) => {
    if (quantityValue === null || quantityValue === undefined) return 'Not specified';
    try {
      // If it's a number
      if (typeof quantityValue === 'number') {
        return quantityValue.toLocaleString();
      }
      // If it's a string that can be converted to a number
      if (typeof quantityValue === 'string') {
        const quantity = Number(quantityValue.replace(/[^0-9]/g, ''));
        if (!isNaN(quantity)) {
          return quantity.toLocaleString();
        }
        return quantityValue; // Return the original string if conversion fails
      }
      // Fallback
      return String(quantityValue);
    } catch (error) {
      console.error('Error formatting quantity:', error);
      return 'Error displaying quantity';
    }
  };
  return <div className="flex flex-col h-[calc(100vh-120px)]" data-id="element-480">
      <div className="bg-white p-4 rounded-lg shadow-sm mb-4" data-id="element-481">
        <h1 className="text-xl font-semibold text-gray-800" data-id="element-482">Review & Launch</h1>
        <p className="text-gray-600 mt-1" data-id="element-483">
          Review your sourcing event details before launching it to suppliers
        </p>
      </div>

      <div className="flex-1 grid grid-cols-1 lg:grid-cols-3 gap-4" data-id="element-484">
        {/* Left column - Event details */}
        <div className="lg:col-span-2 bg-white rounded-lg shadow-sm p-6" data-id="element-485">
          <h2 className="text-lg font-semibold mb-4" data-id="element-486">Event Details</h2>
          <div className="space-y-6" data-id="element-487">
            <div data-id="element-488">
              <div className="flex justify-between items-center" data-id="element-489">
                <h3 className="text-sm font-medium text-gray-500 uppercase" data-id="element-490">
                  Title
                </h3>
                <button className="p-1 text-gray-400 hover:text-blue-600 rounded-full" onClick={() => setEditingTitle(!editingTitle)} data-id="element-491">
                  {editingTitle ? <XIcon size={16} data-id="element-492" /> : <EditIcon size={16} data-id="element-493" />}
                </button>
              </div>
              {editingTitle ? <div className="mt-1 flex" data-id="element-494">
                  <input type="text" className="flex-1 border border-gray-300 rounded-l-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" value={title} onChange={e => setTitle(e.target.value)} data-id="element-495" />
                  <button className="bg-blue-600 text-white px-3 py-2 rounded-r-md hover:bg-blue-700" onClick={saveTitle} data-id="element-496">
                    <SaveIcon size={16} data-id="element-497" />
                  </button>
                </div> : <p className="mt-1 text-gray-800" data-id="element-498">
                  {sourcingEvent.requirements.title}
                </p>}
            </div>

            <div data-id="element-499">
              <div className="flex justify-between items-center" data-id="element-500">
                <h3 className="text-sm font-medium text-gray-500 uppercase" data-id="element-501">
                  Description
                </h3>
                <button className="p-1 text-gray-400 hover:text-blue-600 rounded-full" onClick={() => setEditingDescription(!editingDescription)} data-id="element-502">
                  {editingDescription ? <XIcon size={16} data-id="element-503" /> : <EditIcon size={16} data-id="element-504" />}
                </button>
              </div>
              {editingDescription ? <div className="mt-1 flex" data-id="element-505">
                  <textarea className="flex-1 border border-gray-300 rounded-l-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" value={description} onChange={e => setDescription(e.target.value)} rows={3} data-id="element-506" />
                  <button className="bg-blue-600 text-white px-3 self-stretch rounded-r-md hover:bg-blue-700 flex items-center" onClick={saveDescription} data-id="element-507">
                    <SaveIcon size={16} data-id="element-508" />
                  </button>
                </div> : <p className="mt-1 text-gray-800" data-id="element-509">
                  {sourcingEvent.requirements.description}
                </p>}
            </div>

            <div data-id="element-510">
              <div className="flex justify-between items-center" data-id="element-511">
                <h3 className="text-sm font-medium text-gray-500 uppercase" data-id="element-512">
                  Detailed Requirements
                </h3>
                <button className="p-1 text-gray-400 hover:text-blue-600 rounded-full" onClick={() => setEditingRequirements(!editingRequirements)} data-id="element-513">
                  {editingRequirements ? <XIcon size={16} data-id="element-514" /> : <EditIcon size={16} data-id="element-515" />}
                </button>
              </div>
              {editingRequirements ? <div className="mt-1 flex" data-id="element-516">
                  <textarea className="flex-1 border border-gray-300 rounded-l-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" value={requirementsText} onChange={e => setRequirementsText(e.target.value)} rows={8} data-id="element-517" />
                  <button className="bg-blue-600 text-white px-3 self-stretch rounded-r-md hover:bg-blue-700 flex items-center" onClick={saveRequirements} data-id="element-518">
                    <SaveIcon size={16} data-id="element-519" />
                  </button>
                </div> : <div className="mt-1 p-3 bg-gray-50 rounded-md border border-gray-200 text-sm whitespace-pre-line max-h-60 overflow-y-auto" data-id="element-520">
                  {requirementsText ? requirementsText : `Business Laptop Requirements
Main Components Functionality
CPU/Processor: Should provide efficient multitasking, support office productivity software, video conferencing, and light data analysis. A modern multi-core processor (e.g., Intel Core i5/i7 or AMD Ryzen 5/7) is preferred.
Screen: A clear, anti-glare display with Full HD (1920×1080) resolution for comfortable daily use. Screen size between 14–15.6 inches is ideal for balancing productivity and portability.
Audio Device: Integrated speakers and microphone suitable for video calls and online meetings, with noise-cancellation support recommended. A headphone jack and Bluetooth audio support are desirable.
Memory Space (RAM & Storage): At least 16 GB RAM for smooth multitasking and a minimum of 512 GB SSD storage for fast boot-up, quick file access, and adequate storage of business documents and applications.
Size and Weight
Slim and lightweight design to support portability for travel and commuting.
Target weight: 1.2–1.8 kg.
Thickness: less than 20 mm preferred.
Warranty
A minimum of 3 years warranty covering parts and labor.
On-site or next-business-day support preferred for reduced downtime.`}
                </div>}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-521">
              <div data-id="element-522">
                <div className="flex justify-between items-center" data-id="element-523">
                  <h3 className="text-sm font-medium text-gray-500 uppercase flex items-center" data-id="element-524">
                    <TagIcon size={16} className="mr-1" data-id="element-525" />
                    Quantity
                  </h3>
                  <button className="p-1 text-gray-400 hover:text-blue-600 rounded-full" onClick={() => setEditingQuantity(!editingQuantity)} data-id="element-526">
                    {editingQuantity ? <XIcon size={16} data-id="element-527" /> : <EditIcon size={16} data-id="element-528" />}
                  </button>
                </div>
                {editingQuantity ? <div className="mt-1 flex" data-id="element-529">
                    <input type="number" min="1" className="flex-1 border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" value={quantity} onChange={e => {
                  // Only allow numbers
                  const value = e.target.value.replace(/[^0-9]/g, '');
                  setQuantity(value);
                }} data-id="element-530" />
                    <button className="bg-blue-600 text-white px-3 py-2 rounded-r-md hover:bg-blue-700" onClick={saveQuantity} data-id="element-531">
                      <SaveIcon size={16} data-id="element-532" />
                    </button>
                  </div> : <p className="mt-1 text-gray-800" data-id="element-533">
                    {formatQuantity(sourcingEvent.requirements.quantity)} units
                  </p>}
              </div>

              <div data-id="element-534">
                <div className="flex justify-between items-center" data-id="element-535">
                  <h3 className="text-sm font-medium text-gray-500 uppercase flex items-center" data-id="element-536">
                    <CalendarIcon size={16} className="mr-1" data-id="element-537" />
                    Target Date
                  </h3>
                  <button className="p-1 text-gray-400 hover:text-blue-600 rounded-full" onClick={() => setEditingDate(!editingDate)} data-id="element-538">
                    {editingDate ? <XIcon size={16} data-id="element-539" /> : <EditIcon size={16} data-id="element-540" />}
                  </button>
                </div>
                {editingDate ? <div className="mt-1 border border-gray-200 rounded-md p-3" data-id="element-541">
                    <div className="flex justify-between items-center mb-2" data-id="element-542">
                      <button className="p-1 rounded-full hover:bg-gray-100" onClick={prevMonth} data-id="element-543">
                        <ChevronLeftIcon size={20} data-id="element-544" />
                      </button>
                      <div className="font-medium" data-id="element-545">
                        {currentMonth.toLocaleDateString('en-US', {
                      month: 'long',
                      year: 'numeric'
                    })}
                      </div>
                      <button className="p-1 rounded-full hover:bg-gray-100" onClick={nextMonth} data-id="element-546">
                        <ChevronRightIcon size={20} data-id="element-547" />
                      </button>
                    </div>
                    {/* Calendar Grid */}
                    <div className="grid grid-cols-7 gap-1 text-center mb-1" data-id="element-548">
                      {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(day => <div key={day} className="text-xs font-medium text-gray-500 py-1" data-id="element-549">
                          {day}
                        </div>)}
                    </div>
                    <div className="grid grid-cols-7 gap-1 text-center" data-id="element-550">
                      {/* Empty cells for days before the first day of month */}
                      {Array.from({
                    length: getFirstDayOfMonth(currentMonth.getFullYear(), currentMonth.getMonth())
                  }).map((_, index) => <div key={`empty-${index}`} className="h-7" data-id="element-551"></div>)}
                      {/* Days of the month */}
                      {Array.from({
                    length: getDaysInMonth(currentMonth.getFullYear(), currentMonth.getMonth())
                  }).map((_, index) => {
                    const day = index + 1;
                    const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
                    const isSelected = selectedDate && date.getDate() === selectedDate.getDate() && date.getMonth() === selectedDate.getMonth() && date.getFullYear() === selectedDate.getFullYear();
                    const isToday = new Date().toDateString() === date.toDateString();
                    return <button key={day} className={`h-7 rounded-full flex items-center justify-center text-sm
                              ${isSelected ? 'bg-blue-600 text-white' : isToday ? 'bg-blue-100 text-blue-800' : 'hover:bg-gray-100'}`} onClick={() => handleDateSelection(day)} data-id="element-552">
                            {day}
                          </button>;
                  })}
                    </div>
                    <div className="mt-3 flex justify-end" data-id="element-553">
                      <button className="px-3 py-1 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700" onClick={confirmDateSelection} data-id="element-554">
                        Confirm Date
                      </button>
                    </div>
                  </div> : <p className="mt-1 text-gray-800" data-id="element-555">
                    {formatDate(sourcingEvent.requirements.deadline)}
                  </p>}
              </div>

              <div data-id="element-556">
                <div className="flex justify-between items-center" data-id="element-557">
                  <h3 className="text-sm font-medium text-gray-500 uppercase flex items-center" data-id="element-558">
                    <DollarSignIcon size={16} className="mr-1" data-id="element-559" />
                    Target Budget
                  </h3>
                  <button className="p-1 text-gray-400 hover:text-blue-600 rounded-full" onClick={() => setEditingBudget(!editingBudget)} data-id="element-560">
                    {editingBudget ? <XIcon size={16} data-id="element-561" /> : <EditIcon size={16} data-id="element-562" />}
                  </button>
                </div>
                {editingBudget ? <div className="mt-1 flex" data-id="element-563">
                    <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500" data-id="element-564">
                      €
                    </span>
                    <input type="text" className="flex-1 border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" value={budget} onChange={e => {
                  // Only allow numbers and decimal point
                  const value = e.target.value.replace(/[^0-9.]/g, '');
                  setBudget(value);
                }} data-id="element-565" />
                    <button className="bg-blue-600 text-white px-3 py-2 rounded-r-md hover:bg-blue-700" onClick={saveBudget} data-id="element-566">
                      <SaveIcon size={16} data-id="element-567" />
                    </button>
                  </div> : <p className="mt-1 text-gray-800" data-id="element-568">
                    {formatBudget(sourcingEvent.requirements.budget)}
                  </p>}
              </div>
            </div>

            <div data-id="element-569">
              <div className="flex justify-between items-center" data-id="element-570">
                <h3 className="text-sm font-medium text-gray-500 uppercase" data-id="element-571">
                  Selected Suppliers
                </h3>
                <button className="p-1 text-gray-400 hover:text-blue-600 rounded-full" onClick={() => setShowSupplierModal(true)} data-id="element-572">
                  <EditIcon size={16} data-id="element-573" />
                </button>
              </div>
              <div className="mt-2 space-y-2" data-id="element-574">
                {selectedSuppliers.length > 0 ? selectedSuppliers.map(supplier => <div key={supplier.id} className="flex items-center justify-between p-3 border border-gray-200 rounded-md" data-id="element-575">
                      <div className="flex items-center" data-id="element-576">
                        <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-medium" data-id="element-577">
                          {supplier.name.charAt(0)}
                        </div>
                        <div className="ml-3" data-id="element-578">
                          <p className="text-sm font-medium text-gray-800" data-id="element-579">
                            {supplier.name}
                          </p>
                          <div className="flex flex-wrap gap-1 mt-1" data-id="element-580">
                            {supplier.tags && supplier.tags.slice(0, 2).map((tag, index) => <span key={index} className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full text-xs" data-id="element-581">
                                  {tag}
                                </span>)}
                            {supplier.tags && supplier.tags.length > 2 && <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full text-xs" data-id="element-582">
                                +{supplier.tags.length - 2} more
                              </span>}
                          </div>
                        </div>
                      </div>
                      <button className="p-1 text-gray-400 hover:text-red-600 rounded-full" onClick={() => toggleSupplierSelection(supplier.id)} data-id="element-583">
                        <XIcon size={16} data-id="element-584" />
                      </button>
                    </div>) : <div className="p-3 border border-gray-200 rounded-md bg-gray-50 text-gray-500 flex flex-col items-center py-6" data-id="element-585">
                    <div className="text-blue-500 mb-2" data-id="element-586">
                      <UserIcon size={24} data-id="element-587" />
                    </div>
                    <p className="text-center" data-id="element-588">No suppliers selected yet.</p>
                    <button onClick={handleGoBack} className="mt-3 px-4 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700" data-id="element-589">
                      Go back to select suppliers
                    </button>
                  </div>}
              </div>
            </div>
          </div>
        </div>

        {/* Right column - AI recommendations */}
        <div className="lg:col-span-1 space-y-4" data-id="element-590">
          <div className="bg-white rounded-lg shadow-sm p-6" data-id="element-591">
            <div className="flex items-start" data-id="element-592">
              <div className="bg-blue-100 rounded-full p-2 mr-3" data-id="element-593">
                <ClipboardCheckIcon size={20} className="text-blue-600" data-id="element-594" />
              </div>
              <div data-id="element-595">
                <h2 className="text-lg font-semibold" data-id="element-596">Ready to Launch</h2>
                <p className="text-sm text-gray-600 mt-1" data-id="element-597">
                  Your sourcing event is ready to be sent to suppliers. They
                  will be notified and invited to submit their proposals.
                </p>
              </div>
            </div>
            <div className="mt-6 space-y-3" data-id="element-598">
              <div className="flex items-center" data-id="element-599">
                <CheckCircleIcon size={16} className="text-green-500 mr-2" data-id="element-600" />
                <span className="text-sm" data-id="element-601">Event details complete</span>
              </div>
              <div className="flex items-center" data-id="element-602">
                {selectedSuppliers.length > 0 ? <CheckCircleIcon size={16} className="text-green-500 mr-2" data-id="element-603" /> : <XCircleIcon size={16} className="text-red-500 mr-2" data-id="element-604" />}
                <span className="text-sm" data-id="element-605">
                  {selectedSuppliers.length} suppliers selected
                  {selectedSuppliers.length === 0 && ' - please select suppliers'}
                </span>
              </div>
              <div className="flex items-center" data-id="element-606">
                <CheckCircleIcon size={16} className="text-green-500 mr-2" data-id="element-607" />
                <span className="text-sm" data-id="element-608">Requirements specified</span>
              </div>
              <div className="flex items-center" data-id="element-609">
                <XCircleIcon size={16} className="text-gray-400 mr-2" data-id="element-610" />
                <span className="text-sm text-gray-500" data-id="element-611">
                  No custom questions added
                </span>
              </div>
            </div>
            <div className="mt-6" data-id="element-612">
              <button className={`w-full py-2 ${selectedSuppliers.length > 0 ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-gray-300 text-gray-500 cursor-not-allowed'} rounded-md transition-colors font-medium`} onClick={handleLaunchEvent} disabled={selectedSuppliers.length === 0} data-id="element-613">
                Launch Sourcing Event
              </button>
              <button className="w-full py-2 text-gray-600 hover:text-gray-800 transition-colors mt-2 text-sm" onClick={handleGoBack} data-id="element-614">
                Go Back and Edit
              </button>
            </div>
          </div>
          <div className="bg-gray-50 rounded-lg p-6 border border-gray-200" data-id="element-615">
            <h3 className="font-medium text-gray-800 mb-4" data-id="element-616">
              AI Recommendations
            </h3>
            {/* Requirements advice section */}
            <div className="mb-5 text-sm" data-id="element-617">
              <p className="text-gray-600" data-id="element-618">
                {sourcingEvent.requirements.requirementsText ? 'Your detailed requirements will help suppliers prepare accurate proposals.' : 'Consider adding specific technical requirements to help suppliers prepare accurate proposals.'}
              </p>
              <p className="text-gray-600 mt-2" data-id="element-619">
                {selectedSuppliers.length > 0 ? 'Your selected suppliers have complementary strengths which should result in competitive bidding.' : 'Please select suppliers to continue with the sourcing event.'}
              </p>
            </div>
            {/* Price Comparison */}
            {selectedSuppliers.length > 0 && <div className="mb-5 border-t border-gray-200 pt-4" data-id="element-620">
                <div className="flex justify-between items-center mb-2" data-id="element-621">
                  <h4 className="font-medium text-gray-700" data-id="element-622">
                    Price Comparison
                  </h4>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-623">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h7" data-id="element-624" />
                  </svg>
                </div>
                {/* Mock price data based on selected suppliers */}
                {selectedSuppliers.slice(0, 3).map((supplier, index) => <div key={supplier.id} className="flex justify-between items-center py-1" data-id="element-625">
                    <span className="text-sm text-gray-600" data-id="element-626">
                      {supplier.name}
                    </span>
                    <span className="text-sm font-medium" data-id="element-627">
                      $
                      {(1200 + index * 75 + Math.floor(Math.random() * 50)).toLocaleString()}
                    </span>
                  </div>)}
              </div>}
            {/* Market Insights */}
            <div className="mb-5 border-t border-gray-200 pt-4" data-id="element-628">
              <div className="flex justify-between items-center mb-2" data-id="element-629">
                <h4 className="font-medium text-gray-700" data-id="element-630">Market Insights</h4>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-631">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" data-id="element-632" />
                </svg>
              </div>
              <p className="text-sm text-gray-600 mb-2" data-id="element-633">
                Apple M2 chip supply improving after recent constraints
              </p>
              <div className="bg-blue-50 p-3 rounded-md flex items-start" data-id="element-634">
                <div className="bg-blue-100 rounded-full p-1 mr-2 mt-0.5" data-id="element-635">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-blue-700" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-636">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" data-id="element-637" />
                  </svg>
                </div>
                <p className="text-sm text-blue-700" data-id="element-638">
                  Consider securing AppleCare+ bundle pricing for better value
                  and extended warranty coverage
                </p>
              </div>
            </div>
            {/* Risk Assessment */}
            <div className="border-t border-gray-200 pt-4" data-id="element-639">
              <div className="flex justify-between items-center mb-2" data-id="element-640">
                <h4 className="font-medium text-gray-700" data-id="element-641">Risk Assessment</h4>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-642">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.618 5.984A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016zM12 9v2m0 4h.01" data-id="element-643" />
                </svg>
              </div>
              <div className="space-y-2" data-id="element-644">
                <div className="flex justify-between items-center" data-id="element-645">
                  <span className="text-sm text-gray-600" data-id="element-646">Supply Risk</span>
                  <span className="text-xs font-medium bg-green-100 text-green-800 px-2 py-1 rounded-full" data-id="element-647">
                    Low
                  </span>
                </div>
                <div className="flex justify-between items-center" data-id="element-648">
                  <span className="text-sm text-gray-600" data-id="element-649">Quality Risk</span>
                  <span className="text-xs font-medium bg-green-100 text-green-800 px-2 py-1 rounded-full" data-id="element-650">
                    Low
                  </span>
                </div>
                <div className="flex justify-between items-center" data-id="element-651">
                  <span className="text-sm text-gray-600" data-id="element-652">Delivery Risk</span>
                  <span className="text-xs font-medium bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full" data-id="element-653">
                    Medium
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Supplier Selection Modal */}
      {showSupplierModal && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-id="element-654">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col" data-id="element-655">
            <div className="flex justify-between items-center mb-4" data-id="element-656">
              <h3 className="text-lg font-medium" data-id="element-657">Edit Supplier Selection</h3>
              <button className="text-gray-500 hover:text-gray-700" onClick={() => setShowSupplierModal(false)} data-id="element-658">
                <XIcon size={20} data-id="element-659" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto" data-id="element-660">
              {/* Selected Suppliers */}
              <div className="mb-4" data-id="element-661">
                <h4 className="font-medium text-gray-700 mb-2" data-id="element-662">
                  Selected Suppliers
                </h4>
                <div className="space-y-2" data-id="element-663">
                  {selectedSuppliers.length > 0 ? selectedSuppliers.map(supplier => <div key={supplier.id} className="flex items-center justify-between p-3 border border-gray-200 rounded-md" data-id="element-664">
                        <div className="flex items-center" data-id="element-665">
                          <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-medium" data-id="element-666">
                            {supplier.name.charAt(0)}
                          </div>
                          <div className="ml-3" data-id="element-667">
                            <p className="text-sm font-medium text-gray-800" data-id="element-668">
                              {supplier.name}
                            </p>
                            <div className="flex flex-wrap gap-1 mt-1" data-id="element-669">
                              {supplier.tags && supplier.tags.slice(0, 2).map((tag, index) => <span key={index} className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full text-xs" data-id="element-670">
                                    {tag}
                                  </span>)}
                            </div>
                          </div>
                        </div>
                        <button className="p-1 text-gray-400 hover:text-red-600 rounded-full" onClick={() => toggleSupplierSelection(supplier.id)} data-id="element-671">
                          <XIcon size={16} data-id="element-672" />
                        </button>
                      </div>) : <div className="p-3 border border-gray-200 rounded-md bg-gray-50 text-gray-500 flex flex-col items-center py-6" data-id="element-673">
                      <div className="text-blue-500 mb-2" data-id="element-674">
                        <UserIcon size={24} data-id="element-675" />
                      </div>
                      <p className="text-center" data-id="element-676">No suppliers selected yet.</p>
                      <button onClick={handleGoBack} className="mt-3 px-4 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700" data-id="element-677">
                        Go back to select suppliers
                      </button>
                    </div>}
                </div>
              </div>
              {/* Available Suppliers */}
              <div data-id="element-678">
                <h4 className="font-medium text-gray-700 mb-2" data-id="element-679">
                  Available Suppliers
                </h4>
                <div className="space-y-2" data-id="element-680">
                  {availableSuppliers.length > 0 ? availableSuppliers.map(supplier => <div key={supplier.id} className="flex items-center justify-between p-3 border border-gray-200 rounded-md hover:border-blue-300 cursor-pointer" onClick={() => toggleSupplierSelection(supplier.id)} data-id="element-681">
                        <div className="flex items-center" data-id="element-682">
                          <div className="h-8 w-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 font-medium" data-id="element-683">
                            <UserIcon size={16} data-id="element-684" />
                          </div>
                          <div className="ml-3" data-id="element-685">
                            <p className="text-sm font-medium text-gray-800" data-id="element-686">
                              {supplier.name}
                            </p>
                            <div className="flex flex-wrap gap-1 mt-1" data-id="element-687">
                              {supplier.tags && supplier.tags.slice(0, 2).map((tag, index) => <span key={index} className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full text-xs" data-id="element-688">
                                    {tag}
                                  </span>)}
                            </div>
                          </div>
                        </div>
                        <button className="p-1 text-gray-400 hover:text-blue-600 rounded-full" onClick={e => {
                  e.stopPropagation();
                  toggleSupplierSelection(supplier.id);
                }} data-id="element-689">
                          <PlusIcon size={16} data-id="element-690" />
                        </button>
                      </div>) : <div className="p-3 border border-gray-200 rounded-md bg-gray-50 text-gray-500" data-id="element-691">
                      No more suppliers available.
                    </div>}
                </div>
              </div>
            </div>
            <div className="mt-6 flex justify-end" data-id="element-692">
              <button className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700" onClick={() => setShowSupplierModal(false)} data-id="element-693">
                Done
              </button>
            </div>
          </div>
        </div>}
    </div>;
};
export default ReviewScreen;